import { useState } from "react";
import { AdminLayout } from "../components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  MessageSquare,
  Search,
  Star,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp
} from "lucide-react";

interface AdminResponsesProps {
  onNavigate: (page: string) => void;
}

/**
 * Admin Responses Page
 * Manage reviews and moderation queue
 */
export function AdminResponses({ onNavigate }: AdminResponsesProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("pending");

  // Mock data
  const reviews = {
    pending: [
      {
        id: "1",
        productName: "Adven EMT-2 (Ghost Train Haze)",
        authorName: "John S.",
        stars: 5,
        title: "Excellent quality, great effects",
        body: "This strain has really helped with my chronic pain. Highly recommend.",
        submittedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        isFlagged: false,
        hasBlockedPhrases: false,
      },
      {
        id: "2",
        productName: "Noidecs CBD Oil 20%",
        authorName: "Sarah J.",
        stars: 4,
        title: "Good for anxiety",
        body: "Works well but takes a while to kick in. Price is a bit high.",
        submittedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        isFlagged: false,
        hasBlockedPhrases: false,
      },
      {
        id: "3",
        productName: "Khiron Hindu Kush",
        authorName: "Mike W.",
        stars: 2,
        title: "Not what I expected",
        body: "This product is terrible and a complete waste of money!",
        submittedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        isFlagged: true,
        hasBlockedPhrases: true,
        blockedPhrasesDetected: ["terrible", "waste of money"],
      },
    ],
    approved: [
      {
        id: "4",
        productName: "Adven EMT-2 (Ghost Train Haze)",
        authorName: "Emma L.",
        stars: 5,
        title: "Life changing",
        body: "After trying several products, this one finally worked for me.",
        approvedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        moderatorName: "Admin",
      },
    ],
    rejected: [
      {
        id: "5",
        productName: "Noidecs CBD Oil 20%",
        authorName: "Anonymous",
        stars: 1,
        title: "Spam content",
        body: "Buy cheap alternatives at www.example.com!!!",
        rejectedAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
        moderatorName: "Admin",
        rejectionReason: "Spam/advertising",
      },
    ],
  };

  const stats = {
    pending: reviews.pending.length,
    flagged: reviews.pending.filter(r => r.isFlagged).length,
    approved: reviews.approved.length,
    rejected: reviews.rejected.length,
    avgResponseTime: "2.5 hours",
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderStars = (count: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < count ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'
        }`}
      />
    ));
  };

  return (
    <AdminLayout currentPage="admin-responses" onNavigate={onNavigate}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl">Review Responses</h1>
            <p className="text-muted-foreground">Moderate and manage patient reviews</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold mt-1">{stats.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Flagged</p>
                  <p className="text-2xl font-bold mt-1">{stats.flagged}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-destructive" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Approved</p>
                  <p className="text-2xl font-bold mt-1">{stats.approved}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Rejected</p>
                  <p className="text-2xl font-bold mt-1">{stats.rejected}</p>
                </div>
                <XCircle className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Time</p>
                  <p className="text-lg font-bold mt-1">{stats.avgResponseTime}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="pending">
              Pending ({stats.pending})
            </TabsTrigger>
            <TabsTrigger value="approved">
              Approved ({stats.approved})
            </TabsTrigger>
            <TabsTrigger value="rejected">
              Rejected ({stats.rejected})
            </TabsTrigger>
          </TabsList>

          {/* Pending Tab */}
          <TabsContent value="pending" className="space-y-6">
            {/* Search */}
            <Card>
              <CardContent className="pt-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search reviews..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Reviews List */}
            <div className="space-y-4">
              {reviews.pending.map((review) => (
                <Card key={review.id} className={review.isFlagged ? "border-destructive" : ""}>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {review.isFlagged && (
                              <Badge variant="destructive" className="gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Flagged
                              </Badge>
                            )}
                            {review.hasBlockedPhrases && (
                              <Badge variant="outline" className="border-destructive text-destructive gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Blocked Phrases
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-lg">{review.productName}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex items-center gap-0.5">
                              {renderStars(review.stars)}
                            </div>
                            <span className="text-sm text-muted-foreground">
                              by {review.authorName}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Review Content */}
                      <div>
                        <p className="font-medium mb-1">{review.title}</p>
                        <p className="text-muted-foreground">{review.body}</p>
                      </div>

                      {/* Blocked Phrases Warning */}
                      {review.hasBlockedPhrases && review.blockedPhrasesDetected && (
                        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                          <p className="text-sm font-semibold text-destructive mb-1">
                            Blocked phrases detected:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {review.blockedPhrasesDetected.map((phrase, i) => (
                              <code key={i} className="text-xs bg-destructive/20 px-2 py-0.5 rounded">
                                {phrase}
                              </code>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="text-sm text-muted-foreground">
                          Submitted {formatDate(review.submittedAt)}
                        </span>
                        <div className="flex gap-2">
                          <Button size="sm" variant="default" className="gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Approve
                          </Button>
                          <Button size="sm" variant="outline" className="gap-2 text-destructive hover:text-destructive">
                            <XCircle className="w-4 h-4" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Approved Tab */}
          <TabsContent value="approved" className="space-y-4">
            {reviews.approved.map((review) => (
              <Card key={review.id}>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="bg-primary">Approved</Badge>
                      <h3 className="font-semibold">{review.productName}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-0.5">
                        {renderStars(review.stars)}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        by {review.authorName}
                      </span>
                    </div>
                    <p className="font-medium">{review.title}</p>
                    <p className="text-muted-foreground">{review.body}</p>
                    <p className="text-sm text-muted-foreground pt-2 border-t">
                      Approved {formatDate(review.approvedAt)} by {review.moderatorName}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Rejected Tab */}
          <TabsContent value="rejected" className="space-y-4">
            {reviews.rejected.map((review) => (
              <Card key={review.id}>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive">Rejected</Badge>
                      <h3 className="font-semibold">{review.productName}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-0.5">
                        {renderStars(review.stars)}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        by {review.authorName}
                      </span>
                    </div>
                    <p className="font-medium">{review.title}</p>
                    <p className="text-muted-foreground">{review.body}</p>
                    <div className="pt-2 border-t space-y-1">
                      <p className="text-sm text-destructive">
                        <strong>Reason:</strong> {review.rejectionReason}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Rejected {formatDate(review.rejectedAt)} by {review.moderatorName}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common moderation tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => onNavigate('moderation-queue')}>
                View Full Moderation Queue
              </Button>
              <Button variant="outline" size="sm">
                Blocked Phrases Settings
              </Button>
              <Button variant="outline" size="sm">
                Export Reports
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
